/**
 * BetterBot Chatbot Block
 *
 * Injection block that loads the BetterBot leasing-assistant widget. The
 * vendor snippet pasted on the property looks like:
 *
 *   <script src="https://1801metrow.engine.betterbot.com/loader.js" defer="defer"></script>
 *
 * The only per-property value is the `*.engine.betterbot.com` subdomain —
 * loader.js reads its own script.src to derive the account, fetches a config
 * map, and appends the chat bundle to <body>. So rather than re-injecting
 * whatever blob is pasted we extract the subdomain and build a canonical
 * loader script. The field accepts the full snippet, the loader URL, or the
 * bare subdomain, so the ops team can paste whichever they have on hand.
 *
 * Why singleton + never-tear-down: BetterBot paints its launcher directly
 * into <body> (outside our tracking) and exposes no teardown/destroy API, so
 * re-running the loader duplicates the widget. We inject once and leave it
 * alone for the tab's lifetime, which survives StrictMode and SPA navigation
 * remounts. Subsequent edits to the subdomain won't reflect until a hard
 * refresh — an acceptable trade for not duplicating the widget. This mirrors
 * the entrata-chatbot / anyonehome-chatbot blocks.
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const INJECTED_NODE_ATTRIBUTE = 'data-betterbot-chatbot-node';

// Pull the subdomain out of a loader URL or full snippet, e.g.
// `https://1801metrow.engine.betterbot.com/loader.js` -> `1801metrow`.
const ENGINE_SUBDOMAIN_PATTERN = /(?:https?:\/\/)?([a-z0-9][a-z0-9-]*)\.engine\.betterbot\.com/i;
// Markers that mean we were handed a snippet or URL (so a missing subdomain is
// a real failure, not a bare subdomain to use verbatim).
const SNIPPET_PATTERN = /<script[\s>]|betterbot|https?:\/\//i;
// A bare value must be a plausible DNS label to build a loader URL from.
const SUBDOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;

type BetterBotChatbotState = {
  nodes: Node[];
  value: string | null;
};

function normalizeValue(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * Accept the full embed snippet, the loader URL, or a bare subdomain. If the
 * value contains a `*.engine.betterbot.com` host, use the captured subdomain.
 * Otherwise, if it looks like markup or a URL without one, treat it as
 * unusable (null). A plain string is assumed to be the subdomain itself.
 */
function extractSubdomain(value: string | null | undefined) {
  const normalized = normalizeValue(value);
  if (!normalized) return null;

  const match = normalized.match(ENGINE_SUBDOMAIN_PATTERN);
  const subdomain = normalizeValue(match?.[1]);
  if (subdomain) return subdomain.toLowerCase();

  if (SNIPPET_PATTERN.test(normalized)) return null;
  return SUBDOMAIN_PATTERN.test(normalized) ? normalized.toLowerCase() : null;
}

function createHeadNodes(subdomain: string) {
  // The loader derives the account from its own script.src, then appends the
  // chat bundle to <body> itself — this one canonical script is the whole
  // integration. `defer` from the vendor snippet is meaningless on
  // dynamically-inserted scripts, so it's omitted.
  const loaderScript = document.createElement('script');
  loaderScript.src = `https://${subdomain}.engine.betterbot.com/loader.js`;
  loaderScript.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');

  return [loaderScript];
}

function getBetterBotChatbotState() {
  const windowWithState = window as Window & {
    __betterBotChatbotState?: BetterBotChatbotState;
  };

  // Single tab-wide instance. Once we've injected the loader, we remember it
  // here so subsequent mounts (StrictMode, SPA navigation, etc.) skip
  // re-injection — BetterBot paints its widget directly in <body>, and
  // re-running the loader duplicates the widget.
  if (windowWithState.__betterBotChatbotState) {
    return windowWithState.__betterBotChatbotState;
  }

  const state: BetterBotChatbotState = {
    nodes: [],
    value: null,
  };

  windowWithState.__betterBotChatbotState = state;
  return state;
}

function BetterBotChatbotComponent(_props: BlockProps) {
  const subdomain = extractSubdomain(
    useBlockToken('embed_script', {
      defaultField: 'custom_betterbot_chatbot',
    }),
  );

  useEffect(() => {
    if (!subdomain) return;

    const state = getBetterBotChatbotState();

    // Load the loader exactly once per tab and never tear it down. BetterBot
    // creates its launcher DOM directly in <body> — outside our tracking —
    // and has no teardown API. Tearing down + re-injecting on remount or SPA
    // navigation makes the vendor paint a SECOND widget on top of the
    // existing one, since the original body DOM isn't ours to clean up. The
    // safe-and-simple answer is to behave like a script in index.html: inject
    // once, leave alone for the tab's lifetime. Subsequent edits to the
    // subdomain won't reflect until a hard refresh — an acceptable trade for
    // not duplicating the widget.
    if (state.value) return;

    const nodes = createHeadNodes(subdomain);

    // Mark injected synchronously so a concurrent remount can't inject a
    // second loader before this one finishes.
    state.value = subdomain;
    state.nodes = nodes;

    nodes.forEach(node => document.head.appendChild(node));
  }, [subdomain]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'BetterBotChatbot',
  displayName: 'BetterBot Chatbot',
  tokens: {
    embed_script: {
      label: 'BetterBot Embed Script or Subdomain',
      type: 'text',
      defaultField: 'custom_betterbot_chatbot',
      suggestedFields: ['custom_betterbot_chatbot'],
      description:
        'BetterBot loader snippet, loader URL, or bare *.engine.betterbot.com subdomain (e.g. "1801metrow"). Full snippets are parsed for the subdomain.',
      required: true,
    },
  },
};

export const betterbotChatbotBlock: BlockDefinition = {
  id: 'betterbot-chatbot',
  name: 'BetterBot Chatbot',
  description:
    'BetterBot leasing-assistant widget injected from the property embed snippet or engine subdomain. The floating launcher is the UI — clicking it opens the chat flow.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: BetterBotChatbotComponent,
  contentSchema,
  tags: ['betterbot', 'better bot', 'chatbot', 'chat', 'leasing assistant', 'widget'],
};
